import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import SearchBar from "@/components/SearchBar";
import ListingCard from "@/components/ListingCard";
import CategoryBar from "@/components/CategoryBar";
import WishlistPickerModal from "@/components/WishlistPickerModal";
import { ArrowRight, Sparkles, Shield, Heart } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1765790069270-0043852646b7?crop=entropy&cs=srgb&fm=jpg&w=2000&q=80";

const REGIONS = [
  {
    name: "Finistère",
    tag: "Penn-ar-Bed",
    img: "https://images.unsplash.com/photo-1666883342936-cbfb4c1e7ade?w=1200",
  },
  {
    name: "Côtes-d'Armor",
    tag: "Granit Rose",
    img: "https://images.unsplash.com/photo-1502786129293-79981df4e689?w=1200",
  },
  {
    name: "Morbihan",
    tag: "Mor Bihan",
    img: "https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=1200",
  },
  {
    name: "Ille-et-Vilaine",
    tag: "Saint-Malo · Rennes",
    img: "https://images.unsplash.com/photo-1753601702644-20e2d1a77fd8?w=1200",
  },
];

export default function HomePage() {
  const [listings, setListings] = useState([]);
  const [category, setCategory] = useState(null);
  const [cities, setCities] = useState([]);
  const [picker, setPicker] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const params = category ? { category } : {};
    api.get("/listings", { params }).then(({ data }) => setListings(data.slice(0, 6))).catch(() => {});
  }, [category]);

  useEffect(() => {
    api.get("/cities").then(({ data }) => setCities(data || [])).catch(() => {});
  }, []);

  const onCategoryChange = (c) => {
    setCategory(c);
    if (c) navigate(`/search?category=${c}`);
  };

  return (
    <div className="bg-[#FAFAFA]">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex flex-col">
        <div className="absolute inset-0">
          <img src={HERO} alt="Côte bretonne" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/35 to-black/70" />
        </div>

        <div className="relative z-10 flex-1 flex flex-col">
          <div className="flex-1 flex items-end pb-32 sm:pb-40 px-4 sm:px-6 lg:px-8 pt-32">
            <div className="max-w-7xl mx-auto w-full">
              <div className="max-w-3xl text-white msb-rise">
                <div className="text-[11px] uppercase tracking-[0.35em] mb-6 flex items-center gap-3 text-white/80">
                  <span className="w-8 h-px bg-white/60" />
                  100% Bretagne · Sans intermédiaire géant
                </div>
                <h1 className="font-display font-black leading-[0.95] text-5xl sm:text-6xl lg:text-7xl tracking-tight">
                  Le séjour breton,<br />
                  <em className="italic font-light">comme à la maison.</em>
                </h1>
                <p className="mt-6 text-lg text-white/85 max-w-xl leading-relaxed">
                  Longères de granit, phares au bout du monde, chaumières face au Golfe.
                  Réservez auprès d'hôtes bretons, sans commissions cachées.
                </p>
              </div>
            </div>
          </div>

          <div className="relative -mb-12 px-4 sm:px-6 lg:px-8 msb-rise msb-rise-2">
            <div className="max-w-6xl mx-auto">
              <SearchBar />
            </div>
          </div>
        </div>
      </section>

      {/* REGIONS BENTO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-3">
              Les quatre départements
            </div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
              Explorez par <em className="italic font-light">territoire</em>
            </h2>
          </div>
          <Link to="/search" className="text-sm font-semibold underline underline-offset-4 hover:text-[#1B2A49]">
            Toutes les annonces →
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-6">
          {REGIONS.map((r, i) => (
            <Link
              key={r.name}
              to={`/search?region=${encodeURIComponent(r.name)}`}
              data-testid={`region-${r.name}`}
              className={`img-zoom relative overflow-hidden rounded-lg group ${
                i === 0 ? "md:col-span-7 md:row-span-2 aspect-[4/5] md:aspect-auto md:min-h-[500px]" :
                i === 1 ? "md:col-span-5 aspect-[4/3]" :
                i === 2 ? "md:col-span-5 aspect-[4/3]" :
                "md:col-span-5 md:col-start-8 aspect-[4/3]"
              }`}
            >
              <img src={r.img} alt={r.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 text-white">
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/80 mb-2">{r.tag}</div>
                <div className="font-display text-2xl sm:text-3xl font-bold leading-tight">{r.name}</div>
                <div className="mt-3 text-sm flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  Découvrir <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CATEGORY BAR */}
      <CategoryBar value={category} onChange={onCategoryChange} />

      {/* FEATURED LISTINGS */}
      <section className="bg-[#F5F2EC] py-24 sm:py-32 bg-grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-600 font-bold mb-3">
                Sélection de la semaine
              </div>
              <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
                Les <em className="italic font-light">incontournables</em>
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10">
            {listings.map((l) => <ListingCard key={l.id} listing={l} onWishlistClick={(lst) => setPicker(lst)} />)}
          </div>
        </div>
      </section>

      {/* CITY GUIDES */}
      {cities.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32" data-testid="city-guides-section">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-600 font-bold mb-3">
                Guides locaux
              </div>
              <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
                Explorer la <em className="italic font-light">Bretagne</em> par ville
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {cities.map((c) => (
              <Link
                key={c.slug}
                to={`/ville/${c.slug}`}
                data-testid={`city-card-${c.slug}`}
                className="group block relative aspect-[4/5] overflow-hidden rounded-lg"
              >
                <img src={c.hero} alt={c.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9986A] font-bold mb-1">{c.region}</div>
                  <div className="font-display text-2xl font-bold leading-tight">{c.name}</div>
                  <div className="text-xs text-white/85 mt-1 italic line-clamp-2">{c.tagline}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* CTA OWNER */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
        {/* Trust strip — pillars below the featured grid, less prominent than near the search bar */}
        <div className="border-y border-black/10 py-10 mb-20 grid grid-cols-1 md:grid-cols-3 gap-10">
          {[
            { icon: Sparkles, t: "Sélection bretonne", d: "Chaque hébergement est vérifié, ancré dans son territoire." },
            { icon: Shield, t: "Paiement sécurisé", d: "Réservation et règlement protégés via Stripe." },
            { icon: Heart, t: "Hôtes locaux", d: "Vos sous restent en Bretagne. Kenavo aux mastodontes." },
          ].map(({ icon: Icon, t, d }, i) => (
            <div key={i} className="flex items-start gap-4">
              <div className="w-10 h-10 border border-[#0A0A0A] flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4" />
              </div>
              <div>
                <div className="font-display text-lg font-bold">{t}</div>
                <div className="text-xs text-neutral-600 mt-1 leading-relaxed">{d}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-4">
              Devenir hôte
            </div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05]">
              Votre maison de Bretagne
              <em className="italic font-light"> mérite mieux qu'Airbnb.</em>
            </h2>
            <p className="mt-6 text-neutral-600 text-lg leading-relaxed">
              Pas de commission opaque, pas d'algorithme contre vous. Listez votre bien
              en quelques minutes et reprenez le contrôle de vos locations.
            </p>
            <Link
              to="/become-host"
              data-testid="cta-become-host"
              className="inline-flex items-center gap-3 mt-8 bg-[#0A0A0A] text-white px-8 py-4 hover:bg-[#1B2A49] transition-colors font-semibold"
            >
              Mettre en location <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="img-zoom relative aspect-[4/5] overflow-hidden rounded-lg">
            <img
              src="https://images.unsplash.com/photo-1570199561869-b306d1084b41?w=1200"
              alt="Hôte breton"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {picker && <WishlistPickerModal listing={picker} onClose={() => setPicker(null)} />}
    </div>
  );
}
